import { Group, Text, Textarea } from '@mantine/core';
import { PencilSimple } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TEST_IDS from 'test-ids';

import S from './CardSchedulePrompt.module.css';

import type { ChangeEvent, ReactNode } from 'react';

type CardSchedulePromptProps = {
  prompt: string;
  onChange: (prompt: string) => void;
  disabled?: boolean;
  placeholder?: string;
};

export default function CardSchedulePrompt({
  prompt,
  onChange,
  disabled,
  placeholder,
}: CardSchedulePromptProps): ReactNode {
  const { t } = useTranslation(['Card']);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (disabled) setIsEditing(false);
  }, [disabled]);

  if (isEditing) {
    return (
      <Textarea
        value={prompt}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        onBlur={() => setIsEditing(false)}
        disabled={disabled}
        autosize={false}
        data-test-id={TEST_IDS.cardSchedule.promptInput}
        classNames={{
          root: S.textareaRoot,
          wrapper: S.textareaWrapper,
          input: S.textareaInput,
        }}
      />
    );
  }

  const displayText =
    prompt || placeholder || t('Card:schedule.prompt.placeholder');

  return (
    <Group
      align="flex-start"
      gap="sm"
      wrap="nowrap"
      data-test-id={TEST_IDS.cardSchedule.promptContainer}
      className={clsx(S.promptDisplay, disabled && S.promptDisplayDisabled)}
      onClick={() => !disabled && setIsEditing(true)}
    >
      <Text
        className={S.promptText}
        c={!prompt ? 'dimmed' : undefined}
        fs={!prompt ? 'italic' : undefined}
      >
        {displayText}
      </Text>
      {!disabled && (
        <PencilSimple
          size={16}
          className={S.promptEditIcon}
          data-test-id={TEST_IDS.cardSchedule.promptEdit}
        />
      )}
    </Group>
  );
}
